import { ChangeDetectionStrategy, ChangeDetectorRef, Component, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountVerificationStep } from '@core/models/auth.model';
import { BaseResponse } from '@models/common.model';
import { AuthService } from '@services/auth.service';
import { DestroyService } from '@services/destroy.service';
import { catchError, debounceTime, finalize, of, take, takeUntil } from 'rxjs';

@Component({
    selector: 'pn-account-verification',
    templateUrl: './account-verification.component.html',
    styleUrl: './account-verification.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DestroyService],
})
export class AccountVerificationComponent {
    public verificationStep: AccountVerificationStep = AccountVerificationStep.Success;
    public verificationStepEnum = AccountVerificationStep;
    private readonly _verifyToken: string;
    public loading = signal(false);

    constructor(
        private readonly _router: Router,
        private readonly _authService: AuthService,
        private readonly _route: ActivatedRoute,
        private readonly _cd: ChangeDetectorRef,
        private readonly _destroy$: DestroyService
    ) {
        this._verifyToken = this._route.snapshot.queryParamMap.get('t') || '';
        this._route.data.subscribe((data) => {
            const verify = data['verify'];
            this.verificationStep = verify
                ? AccountVerificationStep.Success
                : AccountVerificationStep.Failure;
        });
        this._authService.session.pipe(takeUntil(this._destroy$)).subscribe((session) => {
            if (session) {
                this._router.navigate(['/']);
            }
        });
    }

    /**
     * Get verify icon
     *
     * @readonly
     * @type {string}
     * @memberof AccountVerificationComponent
     */
    public get verifyIcon(): string {
        return this.verificationStep === AccountVerificationStep.Success
            ? '/assets/icons/account-verify-success.svg'
            : '/assets/icons/account-verify-failed.svg';
    }

    /**
     * Handle sign in
     *
     * @return {*}  {void}
     * @memberof AccountVerificationComponent
     */
    public handleSignIn(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this._authService
            .openSignInDialog()
            .pipe(take(1))
            .subscribe((result) => {
                if (!result) {
                    return;
                }

                this._router.navigate(['/']);
            });
    }

    /**
     * Re-send verify email
     *
     * @memberof AccountVerificationComponent
     */
    public reSendVerifyEmail(): void {
        this.loading.set(true);
        this._authService
            .reSendVerifyEmail(this._verifyToken)
            .pipe(
                debounceTime(500),
                catchError(() => of(new BaseResponse({ ID: -1 }))),
                finalize(() => this.loading.set(false))
            )
            .subscribe((res) => {
                this.verificationStep =
                    res.ID >= 0
                        ? AccountVerificationStep.ResendSuccess
                        : AccountVerificationStep.ResendFailure;
                this._cd.detectChanges();
            });
    }
}
